var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useEffect } from 'react';
import moment from 'moment';
import { useFormContext, useWatch } from 'react-hook-form';
import { ComposerMode } from '../types/index';
import { useEventComposerGlobalContext } from '../composers/EventComposerGlobalContext';
import { useCalendarIntegrationsContext } from '../context/CalendarIntegrationContext';
import { useAdvancedBookingThreshold } from './useAdvancedBookingThreshold';
import { useMaxReservationLength } from './useMaxReservationLength';
import { resolveAllIssues } from '../utils/composerIssues';
import { scrollToHighlight } from '../utils/scrollToComposerSection';
/**
 * The single decision point for whether Save proceeds. All blockers are
 * resolved by the pure catalog in utils/composerIssues.ts — every issue maps
 * to a data-composer-section scroll/highlight target or the blocking Alert,
 * so the footer can never claim "there are errors" without a visible home
 * for them (see ComposerDocs/VALIDATION_REFACTOR_PLAN.md).
 */
export function useFooterValidation(_a) {
    var onSubmit = _a.onSubmit;
    var _b = useFormContext(), control = _b.control, watch = _b.watch;
    var _c = __read(useWatch({
        name: [
            'title',
            'description',
            'recurrenceRule',
            'date',
            'startTime',
            'endTime',
        ],
        control: control,
    }), 6), title = _c[0], description = _c[1], recurrenceRule = _c[2], date = _c[3], startTime = _c[4], endTime = _c[5];
    var _d = useEventComposerGlobalContext(), requestOnlySpace = _d.requestOnlySpace, spacesWithConflicts = _d.spacesWithConflicts, isCheckingConflicts = _d.isCheckingConflicts, requestFormId = _d.requestFormId, spaceRequestFormCompletion = _d.spaceRequestFormCompletion, hasAttemptedSubmit = _d.hasAttemptedSubmit, setHasAttemptedSubmit = _d.setHasAttemptedSubmit, composerMode = _d.composerMode;
    var isSpaceRequestOnlyEvent = !!requestOnlySpace;
    // Any edit clears the submit-attempt flag (footer banner + inline errors
    // gated on it). Deliberate: they are secondary feedback — the blocking
    // Alert and section-level errors persist on their own while the underlying
    // issue remains.
    useEffect(function () {
        var unsubscribe = watch(function () { return setHasAttemptedSubmit(false); }).unsubscribe;
        return unsubscribe;
    }, [watch, setHasAttemptedSubmit]);
    var conflictingSpaceIds = useAdvancedBookingThreshold().conflictingSpaceIds;
    var hasMaxLengthViolation = useMaxReservationLength().hasMaxLengthViolation;
    var _e = useCalendarIntegrationsContext(), canCreateRequestWithPersonalCalendar = _e.canCreateRequestWithPersonalCalendar, isLoadingCalendarIntegration = _e.isLoadingCalendarIntegration;
    var buildInput = function () { return ({
        isCreateMode: composerMode === ComposerMode.CREATE,
        isSpaceRequestOnlyEvent: isSpaceRequestOnlyEvent,
        canCreateRequestWithPersonalCalendar: canCreateRequestWithPersonalCalendar,
        isLoadingCalendarIntegration: isLoadingCalendarIntegration,
        title: title,
        description: description,
        startTime: startTime,
        endTime: endTime,
        date: date,
        recurrenceRule: recurrenceRule,
        now: moment(),
        spacesWithConflicts: spacesWithConflicts,
        conflictingSpaceIds: conflictingSpaceIds,
        hasMaxLengthViolation: hasMaxLengthViolation,
        requestFormId: requestFormId,
        // 'unknown' (query loading / workplace services disabled) counts as
        // complete: this blocker may only fire when the incomplete-form UI is
        // actually visible to scroll to — block only on a proven 'incomplete'.
        isSpaceRequestFormCompleted: spaceRequestFormCompletion !== 'incomplete',
    }); };
    // Render-time resolution drives the footer banner (field-only).
    var fieldIssues = resolveAllIssues(buildInput()).fieldIssues;
    var showValidationError = hasAttemptedSubmit && fieldIssues.length > 0;
    var handleClick = function () {
        // 1. Wait-state: never submit mid-check. Belt-and-braces with the Save
        //    button's loading state — a click can land in the frame before
        //    loading renders. Not an error: no banner, no hasAttemptedSubmit.
        if (isCheckingConflicts)
            return;
        // Re-resolve with a fresh clock: the render-time result can be stale if
        // wall time has drifted past endTime since the last render.
        // (TitleInput's inline help needs no sync here — it derives from
        // hasAttemptedSubmit + isTitleMissing, the same inputs the resolver uses.)
        var _a = resolveAllIssues(buildInput()), blockingAlerts = _a.blockingAlerts, fieldIssues = _a.fieldIssues;
        // 2. External blocker wins: scroll to the always-visible Alert. No footer
        //    banner — the Alert is the primary surface.
        if (blockingAlerts.length > 0) {
            setHasAttemptedSubmit(true);
            scrollToHighlight('blocking-alert');
            return;
        }
        // 3. Field issues: show the banner and scroll to the first (top-most).
        if (fieldIssues.length > 0) {
            setHasAttemptedSubmit(true);
            scrollToHighlight(fieldIssues[0].target);
            return;
        }
        // 4. All clear.
        onSubmit();
    };
    return {
        showValidationError: showValidationError,
        handleClick: handleClick,
        isSpaceRequestOnlyEvent: isSpaceRequestOnlyEvent,
        isCheckingConflicts: isCheckingConflicts,
    };
}
